import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";

export const SORT_OPTIONS = [
  ["taken_desc", "Date taken · newest"], ["taken_asc", "Date taken · oldest"],
  ["added_desc", "Date added · newest"], ["size_desc", "File size · largest"], ["size_asc", "File size · smallest"],
];
const ASPECTS = { landscape: "Landscape", portrait: "Portrait", square: "Square" };

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="filter-field">
    <span>{label}</span>
    <select value={value || ""} onChange={(e) => onChange(e.target.value || undefined)} data-testid={testid}>
      <option value="">Any</option>
      {options.map((o) => <option key={o.value} value={o.value} label={`${o.label || o.value} (${o.count})`} />)}
    </select>
  </label>
);

export const VIS = { private: "Private", family: "Family", guest: "Guest", admin: "Admin" };

export const FilterSheet = ({ open, onOpenChange, space, version, sort, onSort, filters, onFilters }) => {
  const [facets, setFacets] = useState(null);
  useEffect(() => {
    if (open) api.get("/items/facets", { params: { space } }).then((r) => setFacets(r.data));
  }, [open, space, version]);
  const set = (k) => (v) => onFilters({ ...filters, [k]: v });
  const f = facets || { countries: [], cities: [], cameras: [], media_types: [], exts: [], aspects: [] };
  const cities = f.cities.filter((c) => !filters.country || c.country === filters.country);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-sm bg-[#f4f3f0] overflow-y-auto" data-testid="filter-sheet">
        <SheetHeader>
          <SheetTitle className="serif text-2xl">Sort & filter</SheetTitle>
          <SheetDescription>{facets ? `${facets.total} items you can see` : "Loading…"}</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          <Select label="Who can see" value={filters.vis} onChange={set("vis")} testid="filter-btn-visibility"
            options={(f.spaces || []).filter((o) => VIS[o.value]).map((o) => ({ ...o, label: VIS[o.value] }))} />
          <label className="filter-field">
            <span>Sort by</span>
            <select value={sort} onChange={(e) => onSort(e.target.value)} data-testid="sort-select-dropdown">
              {SORT_OPTIONS.map(([v, l]) => <option key={v} value={v} label={l} />)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="filter-field"><span>From</span>
              <input type="date" value={filters.date_from || ""} onChange={(e) => set("date_from")(e.target.value || undefined)} data-testid="filter-date-from" /></label>
            <label className="filter-field"><span>To</span>
              <input type="date" value={filters.date_to || ""} onChange={(e) => set("date_to")(e.target.value || undefined)} data-testid="filter-date-to" /></label>
          </div>
          <Select label="Country" value={filters.country} onChange={(v) => onFilters({ ...filters, country: v, city: undefined })} options={f.countries} testid="filter-btn-country" />
          <Select label="City" value={filters.city} onChange={set("city")} options={cities.map((c) => ({ value: c.value, label: `${c.value}, ${c.country}`, count: c.count }))} testid="filter-btn-city" />
          <Select label="Camera" value={filters.camera} onChange={set("camera")} options={f.cameras} testid="filter-btn-camera" />
          <Select label="Photo / video" value={filters.media_type} onChange={set("media_type")} options={f.media_types.map((o) => ({ ...o, label: o.value === "video" ? "Videos" : "Photos" }))} testid="filter-btn-type" />
          <Select label="File type" value={filters.ext} onChange={set("ext")} options={f.exts.map((o) => ({ ...o, label: o.value.toUpperCase() }))} testid="filter-btn-ext" />
          <Select label="Orientation" value={filters.aspect} onChange={set("aspect")} options={f.aspects.map((o) => ({ ...o, label: ASPECTS[o.value] }))} testid="filter-btn-orientation" />
          <Select label="Event type" value={filters.event_category} onChange={set("event_category")} options={f.categories || []} testid="filter-btn-event-category" />
          <Select label="Event" value={filters.event} onChange={set("event")} options={f.events || []} testid="filter-btn-event" />
          <Select label="Setting" value={filters.setting} onChange={set("setting")} options={f.settings || []} testid="filter-btn-setting" />
          <Select label="Landmark" value={filters.landmark} onChange={set("landmark")} options={f.landmarks || []} testid="filter-btn-landmark" />
          <label className="filter-check">
            <input type="checkbox" checked={Boolean(filters.loved)} onChange={(e) => set("loved")(e.target.checked || undefined)} data-testid="filter-btn-loved" />
            Loved by guests
          </label>
          <label className="filter-check">
            <input type="checkbox" checked={Boolean(filters.date_unknown)} onChange={(e) => set("date_unknown")(e.target.checked || undefined)} data-testid="filter-btn-date-unknown" />
            Date unknown <em>({facets?.date_unknown ?? 0})</em>
          </label>
          <label className="filter-check">
            <input type="checkbox" checked={Boolean(filters.location_unknown)} onChange={(e) => set("location_unknown")(e.target.checked || undefined)} data-testid="filter-btn-location-unknown" />
            Location unknown <em>({facets?.location_unknown ?? 0})</em>
          </label>
          <button className="btn-outline w-full" onClick={() => onFilters({})} data-testid="filter-reset-button">Reset filters</button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
